import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { CircularProgress, Typography } from '@mui/material';
import { billingApi } from '../services/api/billing';
import { useAuthStore } from '../store/authStore';

interface RequireSubscriptionProps {
  children: React.ReactNode;
}

export const RequireSubscription: React.FC<RequireSubscriptionProps> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const { user } = useAuthStore();


  useEffect(() => {
    // Add a small delay to debounce the check and avoid race conditions
    const timeoutId = setTimeout(() => {
      const checkSubscription = async () => {
        if (!user?.organizationId) {
          setLoading(false);
          return;
        }

        try {
          const subscription = await billingApi.getSubscription(user.organizationId);
          
          // Check if subscription exists and is active or in trial
          if (subscription && (subscription.status === 'active' || subscription.status === 'trialing')) {
            setHasSubscription(true);
          } else {
            setHasSubscription(false);
          }
        } catch (error: any) {
          console.error('[RequireSubscription] Error checking subscription:', error);
          // If 404, no subscription exists
          if (error.response?.status === 404) {
            setHasSubscription(false);
          } else if (error.response?.status === 429) {
            // Rate limited - retry after a delay
            setTimeout(() => {
              checkSubscription();
            }, 2000);
          } else {
            // For other errors, let them through (server errors, etc)
            setError('Unable to verify subscription status');
          }
        } finally {
          setLoading(false);
        }
      };

      checkSubscription();
    }, 100); // Small delay to debounce

    return () => clearTimeout(timeoutId);
  }, [user?.organizationId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography variant="body2" style={{ marginLeft: 16 }}>
          Verifying subscription...
        </Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column' }}>
        <Typography variant="h6" color="error" gutterBottom>
          {error}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please try again later
        </Typography>
      </div>
    );
  }

  if (!user?.organizationId) {
    // User doesn't have an organization, redirect to signup
    return <Navigate to="/signup" state={{ from: location }} replace />;
  }

  if (!hasSubscription) {
    // User has an organization but no subscription, redirect to billing setup
    return <Navigate to="/billing/setup" state={{ from: location }} replace />;
  }

  // User has a valid subscription, allow access
  return <>{children}</>;
};

export default RequireSubscription;